import React, { useEffect, useMemo,useRef,  useState } from "react";
import { HiArrowRight } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/LandingPageNew/Navbar";
import API from "../utils/api";
import DesktopSidebar from "../components/LandingPageNew/DesktopSidebar";
const STORAGE_KEY = "howRemoveFlow_v1";

function loadFlow() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveFlow(next) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {}
}

function useHowRemoveFlow() {
  const [flow, setFlow] = useState(() => loadFlow());
  const updateFlow = (patch) => {
    setFlow((prev) => {
      const next = { ...prev, ...patch };
      saveFlow(next);
      return next;
    });
  };
  return { flow, updateFlow };
}

const StepCircle = ({ number, text, active }) => (
  <div className="flex flex-col items-center gap-3 min-w-[90px] text-center">
    <div
      className={`w-[60px] h-[60px] flex items-center justify-center rounded-full text-white text-[24px] font-semibold ${
        active ? "bg-[#0b2b40]" : "bg-[#0b2b40]/30"
      }`}
    >
      {number}
    </div>
    <p
      className={`text-[16px] font-medium ${
        active ? "text-[#0b2b40]" : "text-[#0b2b40]/40"
      }`}
    >
      {text}
    </p>
  </div>
);

const StepLine = ({ active, mobile }) => (
  <div
    className={`rounded-full ${
      mobile ? "w-[35px] h-[4px]" : "w-[130px] h-[4px]"
    } ${
      active
        ? "bg-gradient-to-r from-[#0b2b40] to-white"
        : "bg-gradient-to-r from-[#0b2b40]/30 to-white/30"
    }`}
  />
);

export default function HowRemove2() {
  const navigate = useNavigate();
  const { flow, updateFlow } = useHowRemoveFlow();
  const fetchingRef = useRef(false);
  const requestIdRef = useRef(0);
  const business = flow.business || null;

  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [reviews, setReviews] = useState(flow.reviews || []);
  const [starCount, setStarCount] = useState(
    flow.starCount || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  );

  const [selectedReviews, setSelectedReviews] = useState(
    flow.selectedReviews || []
  );
  const [selectedStars, setSelectedStars] = useState(flow.selectedStars || []);

  const [pricePerDeletion, setPricePerDeletion] = useState(
    flow.pricePerDeletion ?? 129.0
  );
  const [activeTier, setActiveTier] = useState(flow.activeTier ?? 0);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // If no business, user opened step2 directly -> go back step1
  useEffect(() => {
    if (!business) navigate("/howremove1");
  }, [business]);

  // persist key states
  useEffect(() => {
    updateFlow({
      reviews,
      starCount,
      selectedReviews,
      selectedStars,
      pricePerDeletion,
      activeTier,
    });
  }, [
    reviews,
    starCount,
    selectedReviews,
    selectedStars,
    pricePerDeletion,
    activeTier,
  ]);

  const getReviewText = (review) =>
    review?.snippet ||
    review?.extracted_snippet?.original ||
    "Kein Text verfügbar";

  const getAuthorName = (review) => review?.user?.name || "Unbekannter Nutzer";

  const getFormattedDate = (review) => {
    if (review?.iso_date) {
      return new Date(review.iso_date).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
    return review?.date || "Unbekanntes Datum";
  };

  const updatePricing = (count) => {
    let perDeletion = 129.0;
    let tier = 0;
    if (count > 9) {
      perDeletion = 79.0;
      tier = 2;
    } else if (count > 4) {
      perDeletion = 89.0;
      tier = 1;
    }
    setPricePerDeletion(perDeletion);
    setActiveTier(tier);
  };

  const handleReviewSelect = (idx) => {
    setSelectedReviews((prev) => {
      const updated = prev.includes(idx)
        ? prev.filter((i) => i !== idx)
        : [...prev, idx];
      updatePricing(updated.length);
      return updated;
    });
  };

  const handleStarSelect = (star) => {
    setSelectedStars((prevStars) => {
      const isSelected = prevStars.includes(star);
      const updatedStars = isSelected
        ? prevStars.filter((s) => s !== star)
        : [...prevStars, star];

      const matchingIndexes = reviews
        .map((r, idx) => (r.rating === star ? idx : null))
        .filter((idx) => idx !== null);

      setSelectedReviews((prevSelected) => {
        let updatedSelected;
        if (isSelected) {
          updatedSelected = prevSelected.filter(
            (idx) => !matchingIndexes.includes(idx)
          );
        } else {
          updatedSelected = Array.from(
            new Set([...prevSelected, ...matchingIndexes])
          );
        }
        updatePricing(updatedSelected.length);
        return updatedSelected;
      });

      return updatedStars;
    });
  };

 const calculateNewRating = () => {
  if (!business || !reviews) return 0;

  const totalReviews = Number(business.totalReviews || 0);
  const avgRating = Number(business.rating || 0);

  if (totalReviews === 0) return 0;

  // estimated total stars from Google displayed rating
  const estimatedTotalStars = avgRating * totalReviews;

  // stars we are "removing"
  const removedStars = selectedReviews.reduce(
    (acc, idx) => acc + (reviews[idx]?.rating || 0),
    0
  );

  const newTotalReviews = totalReviews - selectedReviews.length;
  if (newTotalReviews <= 0) return 0;

  let newAverage = (estimatedTotalStars - removedStars) / newTotalReviews;

  // ✅ clamp result to valid Google range
  newAverage = Math.max(1, Math.min(5, newAverage));

  return newAverage.toFixed(1);
};


  const PRICING_TIERS = useMemo(
    () => [
      { min: 0, max: 5, price: 129, nextSave: 200 },
      { min: 6, max: 10, price: 89, nextSave: 50 },
      { min: 11, max: Infinity, price: 79, nextSave: 0 },
    ],
    []
  );

  const selectedCount = selectedReviews.length;
  const currentTierData = PRICING_TIERS[activeTier];
  const reviewsLeftForNextTier =
    activeTier < PRICING_TIERS.length - 1
      ? PRICING_TIERS[activeTier].max - selectedCount
      : 0;
  const saveAmount = currentTierData.nextSave;

  const getProgressPercentage = () => {
    if (activeTier === 0) return Math.min((selectedCount / 5) * 100, 100);
    if (activeTier === 1) return Math.min(((selectedCount - 5) / 5) * 100, 100);
    return 100;
  };
const fetchReviewsIfNeeded = async () => {
    if (!business) return;

    // ✅ Stop duplicate calls (StrictMode + rerenders)
    if (fetchingRef.current) return;
    if (reviews && reviews.length > 0) return;

    fetchingRef.current = true;
    const myReqId = ++requestIdRef.current;

    try {
      setReviewsLoading(true);

      const placeId = business.place_id || business.placeId || business.id;
      if (!placeId) throw new Error("Place ID not found");

      const res = await API.get(`/google/place-details/google-maps-reviews2`, {
        params: { place_id: placeId },
      });

      // ✅ If a newer request started, ignore this response
      if (myReqId !== requestIdRef.current) return;

      // ✅ Parse safely (handles multiple backend shapes)
      const payload = res.data;
      const apiReviews =
        payload?.data?.reviews ||
        payload?.reviews ||
        payload?.data?.data?.reviews || // if you nested twice
        [];

      setReviews(apiReviews);

      const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      apiReviews.forEach((r) => {
        if (counts[r.rating] !== undefined) counts[r.rating]++;
      });
      setStarCount(counts);
    } catch (error) {
      console.error("Review fetch error:", error);
    } finally {
      setReviewsLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    fetchReviewsIfNeeded();
   
  }, [business]);

  const nextStep = () => {
    setMobileSidebarOpen(false);
    updateFlow({
      selectedReviews,
      selectedStars,
      pricePerDeletion,
      activeTier,
      reviews,
      starCount,
    });
    navigate("/Bewertungsmanager-3");
  };

  return (
    <>
      <Navbar />
      <section className="relative bg-cover bg-no-repeat font-sans py-4 md:py-8 px-4">
        <div className="flex justify-center mt-14">
          <div className="relative w-full max-w-[1300px] mx-auto p-2 lg:p-10 flex flex-col gap-8">
            <div className="flex w-full gap-10">
              <div className="flex flex-col gap-8 transition-all duration-300 w-full md:w-[72%]">
                {/* Header */}
                <div className="flex flex-col gap-3 mb-10 mt-10 items-center text-center">
                  <h2 className="h2-mb h2-md h2-lg h2-2xl">
                    Unfaire Bewertung(en)? <br />
                    <span className="text-[#0b2b40]">
                      Wie Sie die Löschung einleiten:
                    </span>
                  </h2>
                  <p className="text-gray-500 text-sm md:text-lg w-11/12 md:w-11/12 mt-2 text-center">
                    Unser Bewertungsmanager gibt Ihnen die Kontrolle über Ihren
                    Ruf zurück - ganz risikofrei, denn Sie zahlen nur bei
                    erfolgreicher Löschung.
                  </p>
                </div>

                {/* Steps */}
                <div className="hidden lg:flex justify-center mt-2 gap-10 items-center">
                  <StepCircle
                    active={false}
                    number="1"
                    text="Firmenname eingeben"
                  />
                  <StepLine active />
                  <StepCircle
                    active
                    number="2"
                    text="Negative Bewertung auswählen"
                  />
                  <StepLine active={false} />
                  <StepCircle
                    active={false}
                    number="3"
                    text="Vorgang starten"
                  />
                </div>

                <div className="lg:hidden flex justify-center mt-2 gap-6">
                  <StepCircle
                    active={false}
                    number="1"
                    text="Firmenname eingeben"
                  />
                  <StepLine active mobile />
                  <StepCircle
                    active
                    number="2"
                    text="Negative Bewertung auswählen"
                  />
                  <StepLine active={false} mobile />
                  <StepCircle
                    active={false}
                    number="3"
                    text="Vorgang starten"
                  />
                </div>

                {/* Step 2 content */}
                <div>
                  <div className="flex flex-col md:flex-row gap-8">
                    <div className="flex-1 items-start gap-6">
                      <img
                        src={business?.photoUrl}
                        alt="Company"
                        className="w-full rounded-xl object-cover mb-2"
                        style={{ height: "150px" }}
                      />
                      <div className="flex flex-col">
                        <h2 className="text-2xl font-semibold leading-tight flex flex-wrap items-center gap-2">
                          <span className="text-gray-800">
                            {business?.name?.text}
                          </span>
                          <span className="bg-yellow-100 text-yellow-800 text-sm font-medium px-2 py-1 rounded-full flex items-center mt-2 gap-1">
                            {business?.totalReviews} Bewertung(en)
                          </span>
                        </h2>

                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-gray-800 text-xl font-semibold">
                            {business?.rating?.toFixed(1)}
                          </span>
                          <span className="text-yellow-500 text-4xl">
                            {"★".repeat(Math.round(business?.rating || 0))}
                            {"☆".repeat(5 - Math.round(business?.rating || 0))}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1">
                      <h3 className="font-semibold mb-3 text-lg">
                        Jetzt Bewertungen auswählen
                      </h3>
                      <div className="flex flex-col gap-3">
                        {[1, 2, 3].map((star) => (
                          <label
                            key={star}
                            className="flex items-center justify-between cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={selectedStars.includes(star)}
                                onChange={() => handleStarSelect(star)}
                              />
                              <span>
                                {star} Star ({starCount[star] || 0})
                              </span>
                            </div>
                            <span className="text-yellow-500 text-2xl">
                              {"★".repeat(star) + "☆".repeat(5 - star)}
                            </span>
                          </label>
                        ))}
                      </div>

                      <div className="mt-6 p-2 rounded-2xl bg-gradient-to-r from-green-100 via-green-50 to-white shadow-2xl flex flex-col items-center text-center">
                        <h2 className="text-sm md:text-sm font-semibold leading-tight text-gray-800 mb-4">
                          Nach Optimierung mögliches Top-Rating: <br />
                          <span className="ml-1 font-bold text-xl md:text-2xl bg-gradient-to-r from-green-500 to-green-700 text-transparent bg-clip-text drop-shadow-md">
                            Von: {business?.rating?.toFixed(1)} → auf:{" "}
                            {calculateNewRating()}
                          </span>
                          {Array.from({ length: 5 }).map((_, index) => {
                            const newRating = Number(calculateNewRating());
                            return (
                              <span
                                key={index}
                                className={`text-xl ml-2 ${
                                  index < Math.round(newRating)
                                    ? "text-yellow-500"
                                    : "text-gray-300"
                                }`}
                              >
                                ★
                              </span>
                            );
                          })}
                        </h2>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="relative mt-6 min-h-[200px]">
                    {reviewsLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10 rounded-xl">
                        <div className="flex flex-col items-center gap-3">
                          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#0b2b40] border-t-transparent" />
                          <p className="text-sm text-gray-600">
                            Bewertungen werden geladen...
                          </p>
                        </div>
                      </div>
                    )}

                    {!reviewsLoading && reviews.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {reviews.map((review, idx) => {
                          const reviewText = getReviewText(review);
                          const truncatedReview =
                            reviewText.split(" ").slice(0, 12).join(" ") +
                            (reviewText.split(" ").length > 12 ? "..." : "");

                          const rating = review?.rating || 0;
                          const formattedDate = getFormattedDate(review);

                          return (
                            <div
                              key={review.review_id || idx}
                              onClick={() => handleReviewSelect(idx)}
                              className={`p-4 border rounded-xl relative bg-white cursor-pointer transition ${
                                selectedReviews.includes(idx)
                                  ? "border-blue-500 ring-2 ring-blue-200"
                                  : ""
                              }`}
                            >
                              <div className="flex justify-between mb-2">
                                <span className="text-gray-700 font-medium">
                                  {getAuthorName(review)}
                                </span>
                                <input
                                  type="checkbox"
                                  checked={selectedReviews.includes(idx)}
                                  onChange={() => handleReviewSelect(idx)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>

                              <span className="text-yellow-500 text-lg block">
                                {"★".repeat(rating)}
                                {"☆".repeat(5 - rating)}
                              </span>

                              <p className="pb-4 text-gray-500">
                                {truncatedReview}
                              </p>
                              <p className="text-gray-600 font-semibold text-sm absolute bottom-2">
                                {formattedDate}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {!reviewsLoading && reviews.length === 0 && (
                      <p className="text-center text-gray-500 py-10">
                        Keine negativen Bewertungen gefunden
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT FIXED SIDEBAR (Desktop) */}

              <DesktopSidebar
                currentStep={2}
                nextStep={nextStep}
                business={{ ...business, newRating: calculateNewRating() }}
                selectedReviews={selectedReviews}
                pricePerDeletion={pricePerDeletion}
                activeTier={activeTier}
                reviewsLeftForNextTier={reviewsLeftForNextTier}
                saveAmount={saveAmount}
                getProgressPercentage={getProgressPercentage}
              />
            </div>

            {/* MOBILE: bottom bar to open sidebar */}
            {selectedCount > 0 && (
              <div className="fixed border shadow-lg bottom-0 inset-x-0 bg-white md:hidden">
                <div className="px-4 py-8">
                  <div className="flex mb-4 justify-between align-center">
                    <p className="text-lg font-bold">Preis pro Löschung </p>
                    <h2 className="text-[#0b2b40] text-2xl font-bold">
                      €{pricePerDeletion.toFixed(2)}
                    </h2>
                  </div>
                  <button
                    onClick={() => setMobileSidebarOpen(true)}
                    className="w-full py-3 bg-[#0b2b40] text-white rounded-full flex justify-center items-center gap-3"
                  >
                    Warenkorb öffnen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* MOBILE SLIDE-IN SIDEBAR (kept simple; your desktop content already covers main UX) */}
        <div
          className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${
            mobileSidebarOpen ? "pointer-events-auto" : "pointer-events-none"
          }`}
        >
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
              mobileSidebarOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setMobileSidebarOpen(false)}
          />

          {/* Sidebar panel */}
          <div
            className={`absolute top-0 right-0 h-full w-[90%] max-w-sm bg-gray-100 shadow-xl
           transform transition-transform duration-300 ease-in-out
           ${mobileSidebarOpen ? "translate-x-0" : "translate-x-full"}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-white">
              <h3 className="text-lg font-semibold">Warenkorb</h3>
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>

            {/* Scrollable content */}
            <div className="h-[calc(100%-70px)] overflow-y-auto p-4 flex flex-col gap-4">
              {/* ⬇️ COPY CONTENT FROM DESKTOP SIDEBAR HERE ⬇️ */}

              <div className="block md:hidden fixed h-[100vh] top-0 left-0 w-[100%]  bg-gray-100 rounded flex flex-col">
                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto p-5 scrollbar-hide flex flex-col gap-4">
                  {/* Coupon Code & Price */}
                  <div className="border rounded bg-white p-5">
                    <div className="flex gap-0">
                      <input
                        type="text"
                        placeholder="Gutschein eingeben"
                        className="flex-1 border rounded-l-xl px-4 py-2 outline-none"
                      />
                      <button className="bg-[#0b2b40] ml-[-60px] text-white rounded-r-xl px-4 py-2">
                        einlösen
                      </button>
                    </div>
                    {activeTier < 2 && reviewsLeftForNextTier > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        Nur noch {reviewsLeftForNextTier}{" "}
                        <strong>Bewertungen</strong>
                        {reviewsLeftForNextTier !== 1 && "s"} für die günstigere
                        Preisstaffelung
                      </p>
                    )}
                    {/* Progress Bar */}
                    <div className="mt-4">
                      {/* <div className="flex justify-between text-xs text-gray-500 mb-1">
                                                   <span>Auswahl Fortschritt</span>
                                                   <span>{selectedCount} Reviews</span>
                                               </div> */}

                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0b2b40] transition-all duration-500 ease-out"
                          style={{ width: `${getProgressPercentage()}%` }}
                        />
                      </div>

                      {/* <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                                                   <span>
                                                       {activeTier === 0 && "0 / 5"}
                                                       {activeTier === 1 && "6 / 10"}
                                                       {activeTier === 2 && "10+"}
                                                   </span>
                                                   <span>
                                                       {activeTier === 0 && "Bis zu 5"}
                                                       {activeTier === 1 && "Ab 6"}
                                                       {activeTier === 2 && "Maximal"}
                                                   </span>
                                               </div> */}
                    </div>

                    {activeTier < 2 && saveAmount > 0 && (
                      <p className="text-sm text-bold mt-1 text-gray-500 italic">
                        Sparen Sie €{saveAmount} bei der nächsten Stufe
                      </p>
                    )}

                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {[0, 1, 2].map((tier) => (
                        <div
                          key={tier}
                          className={`border py-3 px-1 pl-2 rounded-xl text-left cursor-pointer ${
                            activeTier === tier
                              ? "bg-[#3c6e711a] text-gray-800 border-1 border-[#3c6e71]"
                              : "bg-white"
                          }`}
                        >
                          <p className="text-sm text-gray-600">
                            {tier === 0
                              ? "Bis zu 5 Löschungen"
                              : tier === 1
                              ? "Ab 6 Löschungen"
                              : "Ab 10 Löschungen"}
                          </p>
                          <p className="text-sm font-semibold text-gray-800">
                            €{tier === 0 ? 129 : tier === 1 ? 89 : 79},00
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Company Info */}
                  <div className="border p-3 rounded-xl bg-gray-50 flex flex-col gap-2 mt-2">
                    <span className="text-sm text-gray-400">
                      Im Falle einer erfolgreichen Löschung der von Ihnen
                      gemeldeten Bewertungen könnte Ihre Google-Bewertung das
                      folgende Rating aufweisen:
                    </span>
                    <img
                      src={business?.photoUrl}
                      alt="Company"
                      className="w-full h-24 object-cover rounded-md"
                    />
                    <p className="font-semibold">{business?.name?.text}</p>
                    <p>
                      Potenzielle Verbesserung auf:{" "}
                      <span className="font-bold">
                        {calculateNewRating()} ★
                      </span>
                    </p>
                    <p className="text-sm text-gray-500">
                      ({selectedReviews.length} selected)
                    </p>
                  </div>
                  <div className="max-w-sm mx-auto">
                    <div className="relative rounded-3xl border border-green-400 bg-gradient-to-br from-green-100 via-green-50 to-white shadow-2xl overflow-hidden transform hover:scale-105 transition-transform duration-500">
                      {/* BEST OPTION BADGE */}
                      <div className="absolute top-0 left-0">
                        <div className="bg-green-500 text-white text-xs font-bold px-4 py-1 rounded-br-2xl flex items-center gap-1 shadow-md">
                          ✓ BESTE OPTION
                        </div>
                      </div>

                      {/* CONTENT */}
                      <div className="p-6 pt-12 text-center relative z-10">
                        {/* TITLE */}
                        {activeTier === 0 && (
                          <p className="text-gray-800 text-lg font-semibold">
                            Ab{" "}
                            <span className="font-boldtext-[#8ec700]">
                              6 Löschungen
                            </span>
                          </p>
                        )}
                        {activeTier === 1 && (
                          <p className="text-gray-800 text-lg font-semibold">
                            Ab{" "}
                            <span className="font-boldtext-[#8ec700]">
                              10 Löschungen
                            </span>
                          </p>
                        )}

                        {/* PRICE */}
                        {activeTier === 0 && (
                          <div className="text-5xl font-extrabold text-gray-900 my-4">
                            € 89
                          </div>
                        )}
                        {activeTier === 1 && (
                          <div className="text-5xl font-extrabold text-gray-900 my-4">
                            € 79
                          </div>
                        )}

                        {/* DISCOUNT */}
                        {activeTier === 0 && (
                          <div className="inline-flex items-center gap-3 bg-green-400 text-white text-sm px-4 py-2 rounded-full mb-4 shadow-sm">
                            <span className="relative font-bold text-lg text-white">
                              € 129
                              <span className="absolute left-0 top-1/2 w-full h-[1px] bg-black transform -translate-y-1/2"></span>
                            </span>
                            <span className="font-semibold text-base">
                              € 89
                            </span>
                            <span className="font-bold ml-1">Sie sparen</span>
                            <span className="bg-green-200 text-green-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                              –31%
                            </span>
                          </div>
                        )}

                        {activeTier === 1 && (
                          <div className="inline-flex items-center gap-3 bg-green-400 text-white text-sm px-2 py-2 rounded-full mb-4 shadow-md">
                            <span className="relative font-bold text-lg text-white">
                              € 89
                              <span className="absolute left-0 top-1/2 w-full h-[1px] bg-black transform -translate-y-1/2"></span>
                            </span>
                            <span className="font-semibold text-base">
                              € 79
                            </span>
                            <span className="font-bold ml-1">Sie sparen</span>
                            <span className="bg-black text-white text-sm font-semibold px-2 py-0.5 rounded-full">
                              –39%
                            </span>
                          </div>
                        )}

                        {/* WARNING */}
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-700 border-t border-dashed pt-4 mt-4">
                          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-green-500 text-white text-xs font-bold">
                            !
                          </div>
                          {activeTier < 2 && reviewsLeftForNextTier >= 0 && (
                            <p className="text-gray-600 leading-tight">
                              Nur noch{" "}
                              <strong>
                                {reviewsLeftForNextTier} Bewertung
                              </strong>
                              {reviewsLeftForNextTier !== 1 && "en"} für die
                              günstigere Preisstaffelung
                            </p>
                          )}
                        </div>

                        {/* PRICE + RATING */}
                        <div className="mt-6">
                          <p className="text-xl font-semibold text-gray-900">
                            €{pricePerDeletion.toFixed(2)}{" "}
                            <span className="text-yellow-400">→ ★★★★★</span>
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            Gesamtbetrag:{" "}
                            <span className="font-semibold text-gray-700">
                              €
                              {(
                                selectedReviews.length * pricePerDeletion
                              ).toFixed(2)}
                            </span>
                          </p>
                        </div>

                        {/* FEATURES */}
                        <div className="mt-6 space-y-3 text-left text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <span className="text-green-500">✔</span>
                            Zahlung nur bei erfolgreicher Löschung
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-500">✔</span>
                            Keine Vorauszahlung
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-500">✔</span>
                            DSGVO-konform
                          </div>
                        </div>

                        {/* BUTTON */}
                        {/* <button className="w-full mt-6 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-3 rounded-xl shadow-lg hover:scale-105 transition transform duration-300">
                                                       Bewertungen jetzt entfernen lassen
                                                   </button> */}
                      </div>

                      {/* Background Overlay for Depth */}
                      <div className="absolute inset-0 bg-gradient-to-t from-green-50 via-white to-transparent opacity-30 pointer-events-none rounded-3xl"></div>
                    </div>
                  </div>
                </div>

                {/* Fixed bottom section */}
                <div className="border-t  p-5 bg-gray-100 flex flex-col gap-2 sticky bottom-0">
                  <div className="border p-3 rounded-xl bg-gray-100">
                    <div className="flex justify-between align-center">
                      {" "}
                      <p className="text-lg font-bold ">Preis pro Löschung</p>
                      <p className="text-lg font-bold">
                        €{pricePerDeletion.toFixed(2)}
                      </p>{" "}
                    </div>
                    <div className="flex justify-between align-center mt-2">
                      <p className="text-sm text-gray-600">Gesamtsumme </p>
                      <h2 className="text-[#0b2b40] text-sm font-bold">
                        €
                        {(selectedReviews.length * pricePerDeletion).toFixed(2)}
                      </h2>
                    </div>
                  </div>

                  <button
                    onClick={nextStep}
                    disabled={selectedReviews.length === 0}
                    className={`w-full py-3 font-semibold rounded-full transition flex gap-8 items-center justify-between px-4
               ${
                 selectedReviews.length === 0
                   ? "bg-[#0b2b40] bg-opacity-40 cursor-not-allowed"
                   : "bg-[#0b2b40] hover:opacity-90 text-white"
               }
           `}
                  >
                    {" "}
                    Jetzt risikofrei buchen
                    <span className="bg-white text-gray-900 p-1 rounded-full flex items-center justify-center">
                      <HiArrowRight className="text-lg transform -rotate-24" />
                    </span>
                  </button>
                  <div className="text-center flex align-center justify-center">
                    <p className="text-xs text-gray-500 mt-1 w-[70%] text-center align-centet flex justify-center ">
                      Kosten entstehen lediglich für Bewertungen, deren
                      Entfernung erfolgreich war.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
